<template>
  <div
    v-loading="loading"
    class="historical-archive-index width-100"
  >
    <Filters
      :filters="historicalArchiveFilters"
      filter-key="historicalArchive"
      :initial-data="filtersStatus.historicalArchive"
    />
    <TableWrapper
      :table-columns="historicalArchiveColumns"
      table-key="historicalArchive"
      :empty-text="$t('table.noResults')"
      @downloadReport="downloadReport"
    />
  </div>
</template>

<script>
import fileSaver from 'file-saver';
import { mapGetters, mapActions } from 'vuex';
import { historicalArchiveColumns, historicalArchiveFilters } from '@/config';
import { historicalArchiveApi } from '@/api';
import lib from '@/lib/functions';
import { extractFilenameFromContentDisposition } from '@/lib/utils';
import TableWrapper from '@/components/TableWrapper.vue';
import Filters from '@/components/Filters.vue';

export default {
  name: 'HistoricalArchiveIndex',
  components: {
    TableWrapper,
    Filters,
  },
  data() {
    return {
      loading: false,
      historicalArchiveColumns,
      historicalArchiveFilters,
    };
  },
  computed: {
    ...mapGetters([
      'filtersStatus',
      'tableFilters',
    ]),
  },
  created() {
    this.parseQueryStringStatus(this.$route.query);
    this.tableRows();
  },
  beforeRouteUpdate(to, from, next) {
    this.parseQueryStringStatus(to.query);
    this.tableRows();
    next();
  },
  methods: {
    ...mapActions([
      'updateTableRows',
      'updateTableCount',
      'parseQueryStringStatus',
    ]),
    async tableRows() {
      try {
        await lib.tableRows('historicalArchive', historicalArchiveApi,
          this.tableFilters('historicalArchive'), this.filtersStatus.historicalArchive,
          this.updateTableRows, this.updateTableCount);
      } catch (error) {
        this.$notify({ showClose: true, type: 'error', message: this.errorMessage(error) });
      }
    },
    /**
     * Download the historical test report PDF
     */
    async downloadReport(row) {
      try {
        this.loading = true;
        const file = await historicalArchiveApi.downloadReport(row.id);

        if (file.data) {
          // Extract filename from Content-Disposition header sent by backend
          const filename = extractFilenameFromContentDisposition(file.headers)
            || `report-${row.reportCode || row.id}.pdf`;

          fileSaver.saveAs(URL.createObjectURL(file.data), filename);
          // Refresh rows so the "already downloaded" indicator reflects this download
          await this.tableRows();
        }

        this.loading = false;
        return file;
      } catch (error) {
        this.loading = false;
        this.$notify({ showClose: true, type: 'error', message: this.errorMessage(error) });
        return error;
      }
    },
    errorMessage(error) {
      const message = error.response?.data?.error?.message;
      if (!message) return this.$t('historicReport.downloadError');
      // Backend keys without a locale entry arrive verbatim (historicReport.*):
      // translate the ones we know, show any other message as it is
      return this.$te(message) ? this.$t(message) : message;
    },
  },
};
</script>

<style lang="scss">
/**
 * Filters.vue lays its columns out on a single CSS grid row: with 11 filters
 * every field ends up too narrow to read.
 * We preferred to do it here so we don't need to touch the Filters.vue.
 * Scoped under .historical-archive-index so no other view is affected.
 */
.historical-archive-index {
  .filter-form {
    > .el-row {
      display: flex !important;
      flex-wrap: wrap;
      gap: 0 1.25rem;
    }

    > .el-row > .el-col {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      flex-shrink: 1;
      width: auto;
      max-width: none;
      padding: 0 !important; // el-row gutter sets this inline, gap replaces it
    }

    // Element UI hardcodes a fixed width on range pickers: without this the
    // date filters ignore their column width and overflow it
    .el-date-editor {
      width: 100%;
    }

    .el-form-item {
      flex: 0 0 auto;
    }

    // min widths, in filter order
    > .el-row > .el-col:nth-child(1) { flex-basis: 170px; } // productCode
    > .el-row > .el-col:nth-child(2) { flex-basis: 180px; } // productName
    > .el-row > .el-col:nth-child(3) { flex-basis: 190px; } // sampleDescription
    > .el-row > .el-col:nth-child(4) { flex-basis: 180px; } // customerSampleCode
    > .el-row > .el-col:nth-child(5) { flex-basis: 150px; } // lot
    > .el-row > .el-col:nth-child(6) { flex-basis: 160px; } // lotDate
    > .el-row > .el-col:nth-child(7) { flex-basis: 160px; } // department
    > .el-row > .el-col:nth-child(8) { flex-basis: 240px; } // acceptanceDate
    > .el-row > .el-col:nth-child(9) { flex-basis: 240px; } // reportIssueDate
    > .el-row > .el-col:nth-child(10) { flex-basis: 160px; } // reportCode
    > .el-row > .el-col:nth-child(11) { flex-basis: 160px; } // alreadyDownloaded

    > .el-row > .el-col.filter-form-buttons {
      flex: 0 0 auto;
    }
  }
}
</style>
